import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useUser } from '../../../contexts/userContext';
import { useColumns } from '../../../contexts/columnsContext';
import api from '../../../services/api';
import axios from 'axios';
import { fileApiUrl } from '../../../config/apiConfig';
import apiUploads from '../../../services/apiUploads';

import Header from '../../../components/Header';
import Mapa from '../../../components/Mapa';
import './style.css';
import Logo from '../../../assets/logo-suite-flow.ico';

import { MdThumbDown, MdThumbUp, MdFilterAltOff, MdAddShoppingCart, MdDescription, MdBarChart, MdRefresh, MdClose } from "react-icons/md";
import { FaEraser, FaCheckDouble } from 'react-icons/fa';

import PreviewCard from '../../PreviewCard';

const isCardPart = (card) =>
  Boolean(card?.card_original_id) ||
  Number(card?.etapa_producao) > 0 ||
  /^(pt|parte)[\s-]*(?:0*[2-9]|0*[1-9][0-9]+)(?:$|[^0-9])/i.test(
    String(card?.name ?? '').trim()
  );

function DashboardPage() {
  const { user, afilhadosList, listAllUsers, userAvatar, getAccessLevel } = useUser();
  const { columns } = useColumns();
  const dashboardTeamUsers = useMemo(() => {
    const visibleUsers = [5, 8].includes(Number(user?.access_level))
      ? (listAllUsers || [])
      : (afilhadosList || []);
    const uniqueUsers = new Map();
    [user, ...visibleUsers].filter(Boolean).forEach((item) => {
      uniqueUsers.set(Number(item.id), item);
    });
    return Array.from(uniqueUsers.values());
  }, [user, afilhadosList, listAllUsers]);
  const [selectedAfilhados, setSelectedAfilhados] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [pendingStartDate, setPendingStartDate] = useState('');
  const [pendingEndDate, setPendingEndDate] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('Pedido de Venda'); // Estado para armazenar o status selecionado
  const [selectedStates, setSelectedStates] = useState([]); // Estado para armazenar os estados selecionados
  const [selectedCities, setSelectedCities] = useState([]); // Estado para armazenar as cidades selecionadas
  const [selectedProducts, setSelectedProducts] = useState([]); // Estado para armazenar os produtos selecionados
  const [selectedOrigins, setSelectedOrigins] = useState([]); // Estado para armazenar as origens selecionadas
  const [clientes, setClientes] = useState([]); // Estado para armazenar os clientes
  const [dashboardCards, setDashboardCards] = useState([]);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(false);
  const [isProcessingDashboard, setIsProcessingDashboard] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [analyticsError, setAnalyticsError] = useState('');
  const [showPreviewCard, setShowPreviewCard] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null); // Armazena o card selecionado
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [analyticsSource, setAnalyticsSource] = useState('crm'); // 'crm' | 'faktory'
  const [selectedFaktoryVendors, setSelectedFaktoryVendors] = useState([]); // string[]
  const [selectedCrmProductsForAnalytics, setSelectedCrmProductsForAnalytics] = useState([]); // string[]
  const [faktoryDocumentTypes, setFaktoryDocumentTypes] = useState([]); // { cd_documento, ds_documento }[]
  const [selectedFaktoryDocumentTypes, setSelectedFaktoryDocumentTypes] = useState([]); // string[] (cd_documento)
  const [compareStartDateA, setCompareStartDateA] = useState('');
  const [compareEndDateA, setCompareEndDateA] = useState('');
  const [compareStartDateB, setCompareStartDateB] = useState('');
  const [compareEndDateB, setCompareEndDateB] = useState('');
  const [selectedAnalyticsEntities, setSelectedAnalyticsEntities] = useState([]);
  const dashboardRequestIdRef = useRef(0);

  // Verifica se o usuário é administrador (tem acesso a 'adm')
  const isAdmin = useMemo(() => {
    if (!user || !getAccessLevel) return false;
    return getAccessLevel('adm') === true;
  }, [user, getAccessLevel]);

  const formatValue = (value) => Number(value || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseInputDateToLocal = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const parseStatusDateToLocal = (statusDateValue) => {
    if (!statusDateValue) return null;
    // Evita problemas de timezone (ex: "2025-12-02T00:00:00.000Z" virar dia anterior no fuso local)
    const raw = String(statusDateValue);
    const datePart = raw.includes('T') ? raw.split('T')[0] : raw.split(' ')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  // Calcula o número de meses entre duas datas (aproximado)
  const calculateMonthsBetween = (startDateString, endDateString) => {
    const start = parseInputDateToLocal(startDateString);
    const end = parseInputDateToLocal(endDateString);
    if (!start || !end) return 1;
    
    const yearDiff = end.getFullYear() - start.getFullYear();
    const monthDiff = end.getMonth() - start.getMonth();
    const dayDiff = end.getDate() - start.getDate();
    
    // Calcula meses totais considerando anos e meses
    let totalMonths = yearDiff * 12 + monthDiff;
    
    // Se a diferença de dias for significativa (mais de 15 dias), considera um mês adicional
    if (dayDiff > 15) {
      totalMonths += 1;
    }
    
    // Retorna pelo menos 1 mês
    return Math.max(1, totalMonths);
  };

  const buildDateParam = (dateString, endOfDay = false) => {
    const date = parseInputDateToLocal(dateString);
    if (!date) return null;
    if (endOfDay) {
      date.setHours(23, 59, 59, 999);
    } else {
      date.setHours(0, 0, 0, 0);
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const milliseconds = String(date.getMilliseconds()).padStart(3, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}`;
  };

  const [selectedMetric, setSelectedMetric] = useState('cost_value'); // Padrão para "cost_value"

  const getPreviousPeriodRange = (start, end, yearOffset = -1) => {
    const startDateObj = parseInputDateToLocal(start);
    const endDateObj = parseInputDateToLocal(end);

    if (!startDateObj || !endDateObj) {
      return { prevStart: '', prevEnd: '' };
    }

    startDateObj.setFullYear(startDateObj.getFullYear() + yearOffset);
    endDateObj.setFullYear(endDateObj.getFullYear() + yearOffset);

    return {
      prevStart: formatDateForInput(startDateObj),
      prevEnd: formatDateForInput(endDateObj),
    };
  };

  // Funções para definir períodos de comparação dentro do mesmo ano
  const setPeriodMonthPrevious = () => {
    const now = new Date();
    // Período B: mês atual
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    // Período A: mês anterior
    const previousMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const previousMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    
    setCompareStartDateA(formatDateForInput(previousMonthStart));
    setCompareEndDateA(formatDateForInput(previousMonthEnd));
    setCompareStartDateB(formatDateForInput(currentMonthStart));
    setCompareEndDateB(formatDateForInput(currentMonthEnd));
  };

  const setPeriodQuarterPrevious = () => {
    const now = new Date();
    const currentQuarter = Math.floor(now.getMonth() / 3);
    // Período B: trimestre atual
    const currentQuarterStart = new Date(now.getFullYear(), currentQuarter * 3, 1);
    const currentQuarterEnd = new Date(now.getFullYear(), (currentQuarter + 1) * 3, 0);
    // Período A: trimestre anterior
    const previousQuarterStart = new Date(now.getFullYear(), (currentQuarter - 1) * 3, 1);
    const previousQuarterEnd = new Date(now.getFullYear(), currentQuarter * 3, 0);
    
    setCompareStartDateA(formatDateForInput(previousQuarterStart));
    setCompareEndDateA(formatDateForInput(previousQuarterEnd));
    setCompareStartDateB(formatDateForInput(currentQuarterStart));
    setCompareEndDateB(formatDateForInput(currentQuarterEnd));
  };

  const setPeriodSemesterPrevious = () => {
    const now = new Date();
    const currentSemester = Math.floor(now.getMonth() / 6);
    // Período B: semestre atual
    const currentSemesterStart = new Date(now.getFullYear(), currentSemester * 6, 1);
    const currentSemesterEnd = new Date(now.getFullYear(), (currentSemester + 1) * 6, 0);
    // Período A: semestre anterior
    const previousSemesterStart = new Date(now.getFullYear(), (currentSemester - 1) * 6, 1);
    const previousSemesterEnd = new Date(now.getFullYear(), currentSemester * 6, 0);
    
    setCompareStartDateA(formatDateForInput(previousSemesterStart));
    setCompareEndDateA(formatDateForInput(previousSemesterEnd));
    setCompareStartDateB(formatDateForInput(currentSemesterStart));
    setCompareEndDateB(formatDateForInput(currentSemesterEnd));
  };

  // Funções para definir períodos de comparação ano atual vs ano passado
  const setPeriodMonthLastYear = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    // Período B: mês atual do ano atual
    const currentMonthStart = new Date(now.getFullYear(), currentMonth, 1);
    const currentMonthEnd = new Date(now.getFullYear(), currentMonth + 1, 0);
    // Período A: mesmo mês do ano passado
    const previousMonthStart = new Date(now.getFullYear() - 1, currentMonth, 1);
    const previousMonthEnd = new Date(now.getFullYear() - 1, currentMonth + 1, 0);
    
    setCompareStartDateA(formatDateForInput(previousMonthStart));
    setCompareEndDateA(formatDateForInput(previousMonthEnd));
    setCompareStartDateB(formatDateForInput(currentMonthStart));
    setCompareEndDateB(formatDateForInput(currentMonthEnd));
  };

  const setPeriodQuarterLastYear = () => {
    const now = new Date();
    const currentQuarter = Math.floor(now.getMonth() / 3);
    // Período B: trimestre atual do ano atual
    const currentQuarterStart = new Date(now.getFullYear(), currentQuarter * 3, 1);
    const currentQuarterEnd = new Date(now.getFullYear(), (currentQuarter + 1) * 3, 0);
    // Período A: mesmo trimestre do ano passado
    const previousQuarterStart = new Date(now.getFullYear() - 1, currentQuarter * 3, 1);
    const previousQuarterEnd = new Date(now.getFullYear() - 1, (currentQuarter + 1) * 3, 0);
    
    setCompareStartDateA(formatDateForInput(previousQuarterStart));
    setCompareEndDateA(formatDateForInput(previousQuarterEnd));
    setCompareStartDateB(formatDateForInput(currentQuarterStart));
    setCompareEndDateB(formatDateForInput(currentQuarterEnd));
  };

  const setPeriodSemesterLastYear = () => {
    const now = new Date();
    const currentSemester = Math.floor(now.getMonth() / 6);
    // Período B: semestre atual do ano atual
    const currentSemesterStart = new Date(now.getFullYear(), currentSemester * 6, 1);
    const currentSemesterEnd = new Date(now.getFullYear(), (currentSemester + 1) * 6, 0);
    // Período A: mesmo semestre do ano passado
    const previousSemesterStart = new Date(now.getFullYear() - 1, currentSemester * 6, 1);
    const previousSemesterEnd = new Date(now.getFullYear() - 1, (currentSemester + 1) * 6, 0);
    
    setCompareStartDateA(formatDateForInput(previousSemesterStart));
    setCompareEndDateA(formatDateForInput(previousSemesterEnd));
    setCompareStartDateB(formatDateForInput(currentSemesterStart));
    setCompareEndDateB(formatDateForInput(currentSemesterEnd));
  };

  const setPeriodYear = () => {
    const now = new Date();
    // Período B: ano atual completo
    const currentYearStart = new Date(now.getFullYear(), 0, 1);
    const currentYearEnd = new Date(now.getFullYear(), 11, 31);
    // Período A: ano passado completo
    const previousYearStart = new Date(now.getFullYear() - 1, 0, 1);
    const previousYearEnd = new Date(now.getFullYear() - 1, 11, 31);
    
    setCompareStartDateA(formatDateForInput(previousYearStart));
    setCompareEndDateA(formatDateForInput(previousYearEnd));
    setCompareStartDateB(formatDateForInput(currentYearStart));
    setCompareEndDateB(formatDateForInput(currentYearEnd));
  };

  useEffect(() => {
    const currentDate = new Date();
    const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

    const defaultStartDate = formatDateForInput(startOfMonth);
    const defaultEndDate = formatDateForInput(endOfMonth);

    setPendingStartDate(defaultStartDate);
    setPendingEndDate(defaultEndDate);
    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);
  }, []);

  useEffect(() => {
    if (!dashboardCards.length) return;

    const cardsWithDiscount = dashboardCards.filter(
      (card) =>
        (card.desconto_externas && Number(card.desconto_externas) > 0) ||
        (card.desconto_outros && Number(card.desconto_outros) > 0)
    );

    if (cardsWithDiscount.length > 0) {
      console.log('[Dashboard] Cards com desconto > 0:', cardsWithDiscount.map(card => ({
        id: card.card_id || card.id,
        entity: card.entity_id,
        desconto_externas: card.desconto_externas,
        desconto_outros: card.desconto_outros
      })));
    } else {
      console.log('[Dashboard] Nenhum card com desconto > 0 foi encontrado neste período.');
    }
  }, [dashboardCards]);

  useEffect(() => {
    if (!startDate || !endDate) return;

    const { prevStart, prevEnd } = getPreviousPeriodRange(startDate, endDate, -1);
    setCompareStartDateA(prevStart);
    setCompareEndDateA(prevEnd);
    setCompareStartDateB(startDate);
    setCompareEndDateB(endDate);
  }, [startDate, endDate]);

  useEffect(() => {
    if (!user || !startDate || !endDate) return;

    const dataInicial = buildDateParam(startDate, false);
    const dataFinal = buildDateParam(endDate, true);

    if (!dataInicial || !dataFinal) return;
    const requestId = dashboardRequestIdRef.current + 1;
    dashboardRequestIdRef.current = requestId;

    const fetchDashboardCards = async () => {
      try {
        setIsLoadingDashboard(true);
        setIsProcessingDashboard(true);
        setDashboardError(null);

        const response = await api.get(
          `/card/find/dashboard/${user.id}/${user.empresa_id}`,
          {
            params: {
              dataInicial,
              dataFinal,
            },
          }
        );

        if (dashboardRequestIdRef.current !== requestId) return;
        const cards = Array.isArray(response.data) ? response.data : [];
        setDashboardCards(cards.filter((card) => !isCardPart(card)));
      } catch (error) {
        if (dashboardRequestIdRef.current !== requestId) return;
        console.error('Erro ao carregar cards do dashboard:', error);
        setDashboardError('Não foi possível carregar os dados do dashboard.');
        setDashboardCards([]);
      } finally {
        if (dashboardRequestIdRef.current === requestId) {
          setIsLoadingDashboard(false);
        }
      }
    };

    fetchDashboardCards();
  }, [user, startDate, endDate]);

  useEffect(() => {
    if (isLoadingDashboard || !isProcessingDashboard) return undefined;

    let firstFrameId;
    let secondFrameId;

    firstFrameId = window.requestAnimationFrame(() => {
      secondFrameId = window.requestAnimationFrame(() => {
        setIsProcessingDashboard(false);
      });
    });

    return () => {
      window.cancelAnimationFrame(firstFrameId);
      if (secondFrameId) {
        window.cancelAnimationFrame(secondFrameId);
      }
    };
  }, [isLoadingDashboard, isProcessingDashboard, dashboardCards]);

  const isDashboardBusy = isLoadingDashboard || isProcessingDashboard;
  const hasPendingDateChanges = pendingStartDate !== startDate || pendingEndDate !== endDate;
  const hasPendingDateRange = Boolean(pendingStartDate && pendingEndDate);
  const isPendingDateRangeInvalid = hasPendingDateRange && pendingStartDate > pendingEndDate;
  const canApplyDateFilter = hasPendingDateChanges && hasPendingDateRange && !isPendingDateRangeInvalid && !isDashboardBusy;

  const handleApplyDateFilter = useCallback(() => {
    if (!pendingStartDate || !pendingEndDate) {
      setDashboardError('Informe a data inicial e final para aplicar o filtro.');
      return;
    }

    if (pendingStartDate > pendingEndDate) {
      setDashboardError('A data inicial não pode ser maior que a data final.');
      return;
    }

    setDashboardError(null);
    setStartDate(pendingStartDate);
    setEndDate(pendingEndDate);
  }, [pendingStartDate, pendingEndDate]);


  const formatName = (name) => {
    return name.toUpperCase().substring(0, 22);
  };

  const truncateText = (text = '', limit = 20) => {
    if (!text) return 'Não informado';
    return text.length > limit ? `${text.substring(0, limit)}...` : text;
  };

  const matchesSelectedStatus = (card) => {
    if (selectedStatus === 'Pedido de Venda') {
      return card.status === 'Vendido' && card.pedido_number && card.pedido_number.length > 0;
    }
    if (selectedStatus === 'Orçamento') {
      return card.status !== 'Vendido' && card.status !== 'Perdido';
    }
    return card.status === selectedStatus;
  };

  const matchesSelectedFilters = (card) => (
    (selectedAfilhados.length === 0 || selectedAfilhados.includes(card.entity_id)) &&
    (selectedStates.length === 0 || selectedStates.includes(card.state)) &&
    (selectedCities.length === 0 || selectedCities.includes(card.city)) &&
    (selectedProducts.length === 0 || selectedProducts.includes(card.produto || 'Não informado')) &&
    (selectedOrigins.length === 0 || selectedOrigins.includes(card.origem || 'Não informado'))
  );

  // const filterCardsByDate = (cards) => {


  //   if (!startDate && !endDate) return cards;
  //   const start = startDate ? new Date(startDate) : new Date('1970-01-01');
  //   const end = endDate ? new Date(endDate) : new Date();
  //   return cards.filter((card) => {
  //     const cardDate = new Date(card.status_date);
  //     return cardDate >= start && cardDate <= end;
  //   });
  // };
  const filterCardsByDate = (cards) => {
    if (!startDate && !endDate) return cards;

    const start = parseInputDateToLocal(startDate) || new Date('1970-01-01');
    const end = parseInputDateToLocal(endDate) || new Date();

    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    return cards.filter((card) => {
      const cardDate = parseStatusDateToLocal(card.status_date);
      if (!cardDate) return false;
      cardDate.setHours(0, 0, 0, 0);

      return cardDate >= start && cardDate <= end;
    });
  };

  // Retorna os cards filtrados por todos os critérios,
  // exceto pelo filtro da dimensão informada (para manter as listas completas)
  const getCardsForDimension = (dimension) => {
    const cards = dashboardCards.filter((card) => {
      if (!matchesSelectedStatus(card)) return false;

      if (dimension !== 'afilhado' && selectedAfilhados.length > 0 && !selectedAfilhados.includes(card.entity_id)) {
        return false;
      }
      if (dimension !== 'state' && selectedStates.length > 0 && !selectedStates.includes(card.state)) {
        return false;
      }
      if (dimension !== 'city' && selectedCities.length > 0 && !selectedCities.includes(card.city)) {
        return false;
      }
      const produtoKey = card.produto || 'Não informado';
      if (dimension !== 'product' && selectedProducts.length > 0 && !selectedProducts.includes(produtoKey)) {
        return false;
      }
      const origemKey = card.origem || 'Não informado';
      if (dimension !== 'origin' && selectedOrigins.length > 0 && !selectedOrigins.includes(origemKey)) {
        return false;
      }

      return true;
    });

    return filterCardsByDate(cards);
  };

  const filteredCardsByAllCriteria = filterCardsByDate(
    dashboardCards.filter((card) => matchesSelectedFilters(card) && matchesSelectedStatus(card))
  );

  const soldCardsInRange = filterCardsByDate(dashboardCards.filter(card =>
    matchesSelectedFilters(card) &&
    card.status === 'Vendido' &&
    card.pedido_number &&
    card.pedido_number.length > 0
  ));

  const budgetCardsInRange = filterCardsByDate(dashboardCards.filter(card =>
    matchesSelectedFilters(card) &&
    (!card.pedido_number || card.pedido_number.length === 0)
  ));

  const conversionRate = budgetCardsInRange.length > 0
    ? (soldCardsInRange.length / budgetCardsInRange.length) * 100
    : 0;

  const averageDiscount = useMemo(() => {
    if (!filteredCardsByAllCriteria.length) return 0;

    // Considera apenas cards com pedido efetuado e valor > 0
    const cardsWithDiscount = filteredCardsByAllCriteria.filter((card) => {
      const discount = Number(card.desconto_externas) || 0;
      const value = parseFloat(card[selectedMetric] || 0);
      const hasPedido = card.pedido_number && card.pedido_number.length > 0;
      return hasPedido && discount > 0 && value > 0;
    });

    if (!cardsWithDiscount.length) return 0;

    const { totalWeightedDiscount, totalValue } = cardsWithDiscount.reduce(
      (acc, card) => {
        const discount = Number(card.desconto_externas) || 0; // percentual
        const value = parseFloat(card[selectedMetric] || 0);  // valor de referência
        return {
          totalWeightedDiscount: acc.totalWeightedDiscount + discount * value,
          totalValue: acc.totalValue + value,
        };
      },
      { totalWeightedDiscount: 0, totalValue: 0 }
    );

    if (!totalValue) return 0;
    // Média ponderada: soma(desconto% * valor) / soma(valor)
    return totalWeightedDiscount / totalValue;
  }, [filteredCardsByAllCriteria, selectedMetric]);




  // const calculateTotalSales = () => {
  //   const filteredCards = filterCardsByDate(cards.filter(card => card.status === selectedStatus));
  //   const salesData = [...afilhadosList, user].map((afilhado) => {
  //     const totalSales = filteredCards
  //       //.filter((card) => card.entity_id === afilhado.id)
  //       //.reduce((acc, card) => acc + parseFloat(card.cost_value), 0);

  //       .filter((card) => card.entity_id === afilhado.id)
  //       .reduce((acc, card) => acc + parseFloat(card[selectedMetric]), 0);


  //     return {
  //       ...afilhado,
  //       totalSales,
  //     };
  //   });

  //   return salesData
  //     .filter((afilhado) => afilhado.totalSales > 0)
  //     .sort((a, b) => b.totalSales - a.totalSales);
  // };



  const calculateTotalSales = () => {
    const filteredCards = getCardsForDimension('afilhado');
  
    const salesData = dashboardTeamUsers
      .filter(Boolean)
      .map((afilhado) => {
        const totalSales = filteredCards
          .filter((card) => card.entity_id === afilhado.id)
          .reduce((acc, card) => acc + parseFloat(card[selectedMetric]), 0);
  
        return {
          ...afilhado,
          totalSales,
        };
      });
  
    // Mantém todos que têm movimento ou estão selecionados; esconde demais
    const visibleSalesData = salesData.filter(
      (afilhado) => afilhado.totalSales > 0 || selectedAfilhados.includes(afilhado.id)
    );
  
    return visibleSalesData.sort((a, b) => b.totalSales - a.totalSales);
  };
  

  // const calculateTotalSalesByState = (selectedAfilhados) => {
  //   const filteredCards = filterCardsByDate(cards.filter(card => card.status === selectedStatus && (selectedAfilhados.length === 0 || selectedAfilhados.includes(card.entity_id))));
  //   const stateSales = {};

  //   filteredCards.forEach((card) => {
  //     if (!stateSales[card.state]) {
  //       stateSales[card.state] = {
  //         state: card.state,
  //         totalSales: 0,
  //         percentageSold: 0,
  //       };
  //     }
  //     //stateSales[card.state].totalSales += parseFloat(card.cost_value);
  //     stateSales[card.state].totalSales += parseFloat(card[selectedMetric]);

  //   });

  //   const totalStateSales = Object.values(stateSales);
  //   const grandTotal = totalStateSales.reduce((acc, state) => acc + state.totalSales, 0);

  //   totalStateSales.forEach((state) => {
  //     state.percentageSold = (state.totalSales / grandTotal) * 100;
  //   });

  //   return totalStateSales.sort((a, b) => b.totalSales - a.totalSales);
  // };



  const calculateTotalSalesByState = () => {
    const filteredCards = getCardsForDimension('state');
  
    const stateSales = {};
  
    filteredCards.forEach((card) => {
      if (!stateSales[card.state]) {
        stateSales[card.state] = {
          state: card.state,
          totalSales: 0,
          percentageSold: 0,
        };
      }
      stateSales[card.state].totalSales += parseFloat(card[selectedMetric]);
    });
  
    const totalStateSales = Object.values(stateSales);
    const grandTotal = totalStateSales.reduce((acc, state) => acc + state.totalSales, 0);
  
    totalStateSales.forEach((state) => {
      state.percentageSold = (state.totalSales / grandTotal) * 100;
    });
  
    return totalStateSales.sort((a, b) => b.totalSales - a.totalSales);
  };
  

  // const calculateTotalSalesByCity = (selectedAfilhados, selectedStates) => {
  //   const filteredCards = filterCardsByDate(cards.filter(card => card.status === selectedStatus &&
  //     (selectedAfilhados.length === 0 || selectedAfilhados.includes(card.entity_id)) &&
  //     (selectedStates.length === 0 || selectedStates.includes(card.state))));

  //   const citySales = {};

  //   filteredCards.forEach((card) => {
  //     if (!citySales[card.city]) {
  //       citySales[card.city] = {
  //         city: card.city,
  //         totalSales: 0,
  //         percentageSold: 0,
  //       };
  //     }
  //     //citySales[card.city].totalSales += parseFloat(card.cost_value);
  //     citySales[card.city].totalSales += parseFloat(card[selectedMetric]);
  //   });

  //   const totalCitySales = Object.values(citySales);
  //   const grandTotal = totalCitySales.reduce((acc, city) => acc + city.totalSales, 0);

  //   totalCitySales.forEach((city) => {
  //     city.percentageSold = (city.totalSales / grandTotal) * 100;
  //   });

  //   return totalCitySales.sort((a, b) => b.totalSales - a.totalSales);
  // };


  const calculateTotalSalesByCity = () => {
    const filteredCards = getCardsForDimension('city');
  
    const citySales = {};
  
    filteredCards.forEach((card) => {
      if (!citySales[card.city]) {
        citySales[card.city] = {
          city: card.city,
          totalSales: 0,
          percentageSold: 0,
        };
      }
      citySales[card.city].totalSales += parseFloat(card[selectedMetric]);
    });
  
    const totalCitySales = Object.values(citySales);
    const grandTotal = totalCitySales.reduce((acc, city) => acc + city.totalSales, 0);
  
    totalCitySales.forEach((city) => {
      city.percentageSold = (city.totalSales / grandTotal) * 100;
    });
  
    return totalCitySales.sort((a, b) => b.totalSales - a.totalSales);
  };
  
  const calculateTotalSalesByProduct = () => {
    const filteredCards = getCardsForDimension('product');

    const productSales = {};

    filteredCards.forEach((card) => {
      const produto = card.produto ? card.produto : 'Não informado';
      if (!productSales[produto]) {
        productSales[produto] = {
          produto,
          totalSales: 0,
          percentageSold: 0,
        };
      }
      productSales[produto].totalSales += parseFloat(card[selectedMetric]);
    });

    const totalProductSales = Object.values(productSales);
    const grandTotal = totalProductSales.reduce((acc, product) => acc + product.totalSales, 0);

    totalProductSales.forEach((product) => {
      product.percentageSold = grandTotal ? (product.totalSales / grandTotal) * 100 : 0;
    });

    return totalProductSales.sort((a, b) => b.totalSales - a.totalSales);
  };



  // const calculateTotalSalesByOrigin = (selectedAfilhados, selectedStates, selectedCities) => {
  //   const filteredCards = filterCardsByDate(cards.filter(card => card.status === selectedStatus &&
  //     (selectedAfilhados.length === 0 || selectedAfilhados.includes(card.entity_id)) &&
  //     (selectedStates.length === 0 || selectedStates.includes(card.state)) &&
  //     (selectedCities.length === 0 || selectedCities.includes(card.city))));

  //   const originSales = {};

  //   filteredCards.forEach((card) => {
  //     const origem = card.origem ? card.origem : 'Não informado';
  //     if (!originSales[origem]) {
  //       originSales[origem] = {
  //         origem: origem,
  //         totalSales: 0,
  //         percentageSold: 0,
  //       };
  //     }
  //     //originSales[origem].totalSales += parseFloat(card.cost_value);
  //     originSales[origem].totalSales += parseFloat(card[selectedMetric]);

  //   });

  //   const totalOriginSales = Object.values(originSales);
  //   const grandTotal = totalOriginSales.reduce((acc, origem) => acc + origem.totalSales, 0);

  //   totalOriginSales.forEach((origem) => {
  //     origem.percentageSold = (origem.totalSales / grandTotal) * 100;
  //   });

  //   return totalOriginSales.sort((a, b) => b.totalSales - a.totalSales);
  // };




  
  const calculateTotalSalesByOrigin = () => {
    const filteredCards = getCardsForDimension('origin');
  
    const originSales = {};
  
    filteredCards.forEach((card) => {
      const origem = card.origem ? card.origem : 'Não informado';
      if (!originSales[origem]) {
        originSales[origem] = {
          origem: origem,
          totalSales: 0,
          percentageSold: 0,
        };
      }
      originSales[origem].totalSales += parseFloat(card[selectedMetric]);
    });
  
    const totalOriginSales = Object.values(originSales);
    const grandTotal = totalOriginSales.reduce((acc, origem) => acc + origem.totalSales, 0);
  
    totalOriginSales.forEach((origem) => {
      origem.percentageSold = (origem.totalSales / grandTotal) * 100;
    });
  
    return totalOriginSales.sort((a, b) => b.totalSales - a.totalSales);
  };
  











  const totalSalesData = calculateTotalSales();

  const calculateTotal = (data) => {
    return data.reduce((acc, afilhado) => acc + afilhado.totalSales, 0);
  };

  const handleAfilhadoClick = (afilhado) => {
    setSelectedAfilhados(prevSelected => {
      if (prevSelected.includes(afilhado.id)) {
        return prevSelected.filter(id => id !== afilhado.id);
      } else {
        return [...prevSelected, afilhado.id];
      }
    });
  };

  const handleStatusClick = (status) => {
    setSelectedStatus(status);
  };

  const clearListas = () => {
    setSelectedAfilhados([]);
    setSelectedStates([]);
    setSelectedCities([]);
    setSelectedProducts([]);
    setSelectedOrigins([]);
  }

  const handleStateClick = (state) => {
    setSelectedStates(prevSelected => {
      if (prevSelected.includes(state)) {
        return prevSelected.filter(s => s !== state);
      } else {
        return [...prevSelected, state];
      }
    });
  };

  const handleCityClick = (city) => {
    setSelectedCities(prevSelected => {
      if (prevSelected.includes(city)) {
        return prevSelected.filter(c => c !== city);
      } else {
        return [...prevSelected, city];
      }
    });
  };

  const handleProductClick = (product) => {
    setSelectedProducts(prevSelected => {
      if (prevSelected.includes(product)) {
        return prevSelected.filter(p => p !== product);
      } else {
        return [...prevSelected, product];
      }
    });
  };

  const handleOriginClick = (origem) => {
    setSelectedOrigins(prevSelected => {
      if (prevSelected.includes(origem)) {
        return prevSelected.filter(o => o !== origem);
      } else {
        return [...prevSelected, origem];
      }
    });
  };

  const getAnalyticsUserIds = () => {
    if (selectedAnalyticsEntities.length > 0) {
      return selectedAnalyticsEntities;
    }
    if (selectedAfilhados.length > 0) {
      return selectedAfilhados;
    }
    return dashboardTeamUsers.map((entity) => entity.id).filter(Boolean);
  };

  const handleAnalyticsEntityClick = (entityId) => {
    setSelectedAnalyticsEntities((prevSelected) => {
      if (prevSelected.includes(entityId)) {
        return prevSelected.filter((id) => id !== entityId);
      }
      return [...prevSelected, entityId];
    });
  };

  const handleClearAnalyticsSelection = () => {
    setSelectedAnalyticsEntities([]);
  };

  const handleSelectAllAnalyticsEntities = () => {
    const allIds = dashboardTeamUsers.map((entity) => entity.id);
    setSelectedAnalyticsEntities(allIds);
  };

  const crmProductOptionsForAnalytics = useMemo(() => {
    if (analyticsSource !== 'crm') return [];
    const products = (dashboardCards || [])
      .map((c) => String(c?.produto || 'Não informado'))
      .filter(Boolean);
    return Array.from(new Set(products)).sort((a, b) => a.localeCompare(b));
  }, [analyticsSource, dashboardCards]);

  const handleCrmProductAnalyticsClick = (product) => {
    setSelectedCrmProductsForAnalytics((prev) => {
      if (prev.includes(product)) return prev.filter((p) => p !== product);
      return [...prev, product];
    });
  };

  const clearCrmProductAnalyticsSelection = () => setSelectedCrmProductsForAnalytics([]);

  const selectAllCrmProductsAnalytics = () => setSelectedCrmProductsForAnalytics(crmProductOptionsForAnalytics);

  const handleFaktoryDocumentTypeClick = (cdDocumento) => {
    setSelectedFaktoryDocumentTypes((prev) => {
      if (prev.includes(cdDocumento)) return prev.filter((d) => d !== cdDocumento);
      return [...prev, cdDocumento];
    });
  };

  const clearFaktoryDocumentTypes = () => setSelectedFaktoryDocumentTypes([]);

  const selectAllFaktoryDocumentTypes = () => {
    const all = (faktoryDocumentTypes || [])
      .map((d) => String(d.cd_documento || d.CD_DOCUMENTO || '').trim())
      .filter(Boolean);
    setSelectedFaktoryDocumentTypes(all);
  };

  const faktoryVendorOptions = useMemo(() => {
    if (analyticsSource !== 'faktory') return [];
    const users = analyticsData?.users || [];
    return users
      .map((u) => ({
        id: String(u.id ?? u.username ?? u.name ?? ''),
        name: String(u.username ?? u.name ?? u.id ?? 'Vendedor'),
      }))
      .filter((v) => v.id);
  }, [analyticsSource, analyticsData]);

  const handleFaktoryVendorClick = (vendorId) => {
    setSelectedFaktoryVendors((prev) => {
      if (prev.includes(vendorId)) {
        return prev.filter((id) => id !== vendorId);
      }
      return [...prev, vendorId];
    });
  };

  const handleClearFaktoryVendors = () => setSelectedFaktoryVendors([]);

  const handleSelectAllFaktoryVendors = () => {
    const allIds = faktoryVendorOptions.map((v) => v.id);
    setSelectedFaktoryVendors(allIds);
  };

  const displayAnalyticsData = useMemo(() => {
    if (analyticsSource !== 'faktory') return analyticsData;
    if (!analyticsData?.users?.length) return analyticsData;
    if (!selectedFaktoryVendors.length) return analyticsData;

    const filteredUsers = analyticsData.users.filter((u) =>
      selectedFaktoryVendors.includes(String(u.id ?? u.username ?? u.name ?? ''))
    );

    const totals = {
      periodA: {
        realized: filteredUsers.reduce((acc, u) => acc + (Number(u.periodA?.realized) || 0), 0),
        pedidos: filteredUsers.reduce((acc, u) => acc + (Number(u.periodA?.pedidos) || 0), 0),
        meta: 0,
        discount: 0,
      },
      periodB: {
        realized: filteredUsers.reduce((acc, u) => acc + (Number(u.periodB?.realized) || 0), 0),
        pedidos: filteredUsers.reduce((acc, u) => acc + (Number(u.periodB?.pedidos) || 0), 0),
        meta: 0,
        discount: 0,
      },
    };

    return { ...analyticsData, users: filteredUsers, totals };
  }, [analyticsSource, analyticsData, selectedFaktoryVendors]);

  const analyticsGroupTotals = useMemo(() => {
    if (!displayAnalyticsData?.totals) {
      return {
        meta: Number(user?.meta_grupo) || 0,
        metaPeriodA: 0,
        metaPeriodB: 0,
        periodA: 0,
        periodB: 0,
        discountA: 0,
        discountB: 0,
        achievementA: 0,
        achievementB: 0,
        diffValue: 0,
        diffPercent: 0,
      };
    }
    
    // Calcula meses para cada período
    const monthsPeriodA = compareStartDateA && compareEndDateA ? calculateMonthsBetween(compareStartDateA, compareEndDateA) : 1;
    const monthsPeriodB = compareStartDateB && compareEndDateB ? calculateMonthsBetween(compareStartDateB, compareEndDateB) : 1;
    
    // Multiplica meta_grupo pelo número de meses de cada período
    const baseMetaGrupo = Number(user?.meta_grupo) || 0;
    const metaPeriodA = baseMetaGrupo * monthsPeriodA;
    const metaPeriodB = baseMetaGrupo * monthsPeriodB;

    const periodARealized = displayAnalyticsData.totals.periodA.realized || 0;
    const periodBRealized = displayAnalyticsData.totals.periodB.realized || 0;

    const achievementA = metaPeriodA > 0 ? (periodARealized / metaPeriodA) * 100 : 0;
    const achievementB = metaPeriodB > 0 ? (periodBRealized / metaPeriodB) * 100 : 0;

    const diffValue = periodBRealized - periodARealized;
    const diffPercent = periodARealized > 0 ? (diffValue / periodARealized) * 100 : 0;
    
    return {
      meta: baseMetaGrupo, // Meta base (mensal)
      metaPeriodA,
      metaPeriodB,
      periodA: periodARealized,
      periodB: periodBRealized,
      discountA: displayAnalyticsData.totals.periodA.discount || 0,
      discountB: displayAnalyticsData.totals.periodB.discount || 0,
      achievementA,
      achievementB,
      diffValue,
      diffPercent,
    };
  }, [displayAnalyticsData, user, compareStartDateA, compareEndDateA, compareStartDateB, compareEndDateB]);

  const filteredTotalSalesData = selectedAfilhados.length > 0
    ? totalSalesData.filter(data => selectedAfilhados.includes(data.id))
    : totalSalesData;

  const filteredTotal = calculateTotal(filteredTotalSalesData);

  const filteredTotalSalesDataByState = calculateTotalSalesByState();

  const filteredTotalSalesDataByCity = calculateTotalSalesByCity();

  const filteredTotalSalesDataByProduct = calculateTotalSalesByProduct();

  const filteredTotalSalesDataByOrigin = calculateTotalSalesByOrigin();

  filteredTotalSalesData.forEach(afilhado => {
    afilhado.percentageSold = (afilhado.totalSales / filteredTotal) * 100;
  });

  const maxPercentage = Math.max(...filteredTotalSalesData.map(afilhado => afilhado.percentageSold || 0));




  const filteredTotalCardSales = filteredCardsByAllCriteria.reduce((acc, card) => acc + parseFloat(card[selectedMetric] || 0), 0);
  const obrasCount = filteredCardsByAllCriteria.length;

  // const sortedCardsByPercentage = filteredCardsByAllCriteria
  //   .map(card => {
  //     const percentageSold = (parseFloat(card.cost_value) / filteredTotalCardSales) * 100 || 0;
  //     return { ...card, percentageSold };
  //   })
  //   .sort((a, b) => b.percentageSold - a.percentageSold); // Ordena em ordem decrescente
  const sortedCardsByPercentage = filteredCardsByAllCriteria
    .map(card => {
      const percentageSold = (parseFloat(card[selectedMetric]) / filteredTotalCardSales) * 100 || 0; // Ajustado
      return { ...card, percentageSold };
    })
    .sort((a, b) => b.percentageSold - a.percentageSold);

  const fetchAnalyticsData = useCallback(async () => {
    if (!user) return;
    if (!compareStartDateA || !compareEndDateA || !compareStartDateB || !compareEndDateB) return;

    const periodAStart = buildDateParam(compareStartDateA, false);
    const periodAEnd = buildDateParam(compareEndDateA, true);
    const periodBStart = buildDateParam(compareStartDateB, false);
    const periodBEnd = buildDateParam(compareEndDateB, true);

    if (!periodAStart || !periodAEnd || !periodBStart || !periodBEnd) return;

    setIsLoadingAnalytics(true);
    setAnalyticsError('');

    try {
      if (analyticsSource === 'faktory') {
        const response = await apiUploads.post('/faktory/pedidovenda/analytics/compare', {
          periodA: { dataInicial: periodAStart, dataFinal: periodAEnd },
          periodB: { dataInicial: periodBStart, dataFinal: periodBEnd },
          tipoDocumento: 'PV',
          tiposDocumento: selectedFaktoryDocumentTypes.length ? selectedFaktoryDocumentTypes : undefined,
        });
        setAnalyticsData(response.data);
      } else {
        const targetUserIds = getAnalyticsUserIds();
        if (!targetUserIds.length) return;

        const response = await api.post(`/card/dashboard/analytics/compare/${user.id}/${user.empresa_id}`, {
          periodA: { dataInicial: periodAStart, dataFinal: periodAEnd },
          periodB: { dataInicial: periodBStart, dataFinal: periodBEnd },
          userIds: targetUserIds,
          products: selectedCrmProductsForAnalytics.length ? selectedCrmProductsForAnalytics : undefined,
        });
        setAnalyticsData(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar gráficos do dashboard:', error);
      setAnalyticsError('Não foi possível carregar os dados. Tente novamente.');
    } finally {
      setIsLoadingAnalytics(false);
    }
  }, [
    user,
    compareStartDateA,
    compareEndDateA,
    compareStartDateB,
    compareEndDateB,
    selectedAfilhados,
    dashboardTeamUsers,
    selectedAnalyticsEntities,
    analyticsSource,
    fileApiUrl,
    selectedCrmProductsForAnalytics,
    selectedFaktoryDocumentTypes
  ]);

  // Garante que usuários não-admin não possam usar Faktory
  useEffect(() => {
    if (!isAdmin && analyticsSource === 'faktory') {
      setAnalyticsSource('crm');
    }
  }, [isAdmin, analyticsSource]);

  useEffect(() => {
    if (showAnalyticsModal) {
      fetchAnalyticsData();
    }
  }, [showAnalyticsModal, fetchAnalyticsData]);

  useEffect(() => {
    if (!showAnalyticsModal) return;
    if (analyticsSource !== 'faktory') return;

    let isMounted = true;
    (async () => {
      try {
        const response = await apiUploads.get('/faktory/pedidovenda/document-types');
        if (!isMounted) return;
        setFaktoryDocumentTypes(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Erro ao carregar tipos de documento do Faktory:', error);
        if (!isMounted) return;
        setFaktoryDocumentTypes([]);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [showAnalyticsModal, analyticsSource]);

  const analyticsComparisonList = useMemo(() => {
    if (!displayAnalyticsData?.users?.length) return [];
    
    // Calcula meses para cada período
    const monthsPeriodA = compareStartDateA && compareEndDateA ? calculateMonthsBetween(compareStartDateA, compareEndDateA) : 1;
    const monthsPeriodB = compareStartDateB && compareEndDateB ? calculateMonthsBetween(compareStartDateB, compareEndDateB) : 1;
    
    // Calcula valores máximos considerando metas multiplicadas
    const maxValue = displayAnalyticsData.users.reduce((max, user) => {
      const baseMeta = Number(user.meta) || 0;
      const metaPeriodA = baseMeta * monthsPeriodA;
      const metaPeriodB = baseMeta * monthsPeriodB;
      const values = [
        metaPeriodA,
        metaPeriodB,
        Number(user.periodA?.realized) || 0,
        Number(user.periodB?.realized) || 0,
      ];
      return Math.max(max, ...values);
    }, 0) || 1;

    return displayAnalyticsData.users.map((user) => {
      const baseMeta = Number(user.meta) || 0;
      const metaPeriodA = baseMeta * monthsPeriodA;
      const metaPeriodB = baseMeta * monthsPeriodB;
      const periodARealized = Number(user.periodA?.realized) || 0;
      const periodBRealized = Number(user.periodB?.realized) || 0;

      const achievementA = metaPeriodA > 0 ? (periodARealized / metaPeriodA) * 100 : 0;
      const achievementB = metaPeriodB > 0 ? (periodBRealized / metaPeriodB) * 100 : 0;
      const diffValue = periodBRealized - periodARealized;
      const diffPercent = periodARealized > 0 ? (diffValue / periodARealized) * 100 : 0;
      
      return {
        id: user.id,
        name: user.username || `Usuário ${user.id}`,
        meta: baseMeta, // Meta base (mensal) para referência
        metaPeriodA: metaPeriodA, // Meta ajustada para período A
        metaPeriodB: metaPeriodB, // Meta ajustada para período B
        periodA: periodARealized,
        periodB: periodBRealized,
        discountA: Number(user.periodA?.discount) || 0,
        discountB: Number(user.periodB?.discount) || 0,
        metaPeriodAWidth: (metaPeriodA / maxValue) * 100,
        metaPeriodBWidth: (metaPeriodB / maxValue) * 100,
        periodAWidth: (periodARealized / maxValue) * 100,
        periodBWidth: (periodBRealized / maxValue) * 100,
        achievementA,
        achievementB,
        diffValue,
        diffPercent,
      };
    });
  }, [displayAnalyticsData, compareStartDateA, compareEndDateA, compareStartDateB, compareEndDateB]);



  const handleCardClick = (card) => {
    setSelectedCard(card);
    setShowPreviewCard(true); // Exibe o modal ao selecionar o card
  };


  return (
    <div className='dashboard-container'>
      <Header />
      {isDashboardBusy && (
        <div className="dashboardpage-loading-overlay" role="status" aria-live="polite" aria-label="Carregando dashboard">
          <div className="dashboardpage-loading-card">
            <div className="dashboardpage-loading-spinner" />
            <div className="dashboardpage-loading-text">Carregando dashboard...</div>
          </div>
        </div>
      )}
      <div className='dashboard-header'>
        {/* <img className='logo-dashboard' src={user && user.avatar ? user.avatar : Logo} alt='Logo' /> */}
        <img
          className='logo-dashboard'
          src={user && userAvatar ? (userAvatar?.includes('syncs-avatar') ? require(`../../../assets/avatares/${userAvatar}`) : user.avatar) : Logo}
          alt='Logo'
        />


        <div className='dashboard-total-sales'>
          <h2>Total</h2>
          <h2>{formatValue(filteredTotal)}</h2>
          <span className='dashboard-total-works'>{obrasCount} {obrasCount === 1 ? 'obra' : 'obras'}</span>
        </div>

        <div className='date-filters-dashboard'>
          <div className='date-column-dashboard'>
            <label className='label-date-dashboard' htmlFor='dashboard-start-date'>Data Inicial</label>
            <input
              id='dashboard-start-date'
              type="date"
              value={pendingStartDate}
              onChange={(e) => setPendingStartDate(e.target.value)}
              className='input-date-dashboard'
            />
          </div>
          <div className='date-column-dashboard'>
            <label className='label-date-dashboard' htmlFor='dashboard-end-date'>Data Final</label>
            <input
              id='dashboard-end-date'
              type="date"
              value={pendingEndDate}
              onChange={(e) => setPendingEndDate(e.target.value)}
              className='input-date-dashboard'
            />
          </div>
          <button
            type='button'
            className='apply-date-filter-dashboard'
            onClick={handleApplyDateFilter}
            disabled={!canApplyDateFilter}
            title={isPendingDateRangeInvalid ? 'A data inicial não pode ser maior que a final' : 'Aplicar período'}
          >
            <FaCheckDouble aria-hidden='true' />
            <span>Aplicar</span>
          </button>
        </div>

        {/* <div className='status-container-dashboard'>
          <MdThumbUp
            className={`icon-perdido-dashboard ${selectedStatus === 'Vendido' ? 'active' : ''}`}
            onClick={() => handleStatusClick('Vendido')}
            style={{ color: selectedStatus === 'Vendido' ? 'dodgerblue' : 'gray' }}
          />
          <MdThumbDown
            className={`icon-vendido-dashboard ${selectedStatus === 'Perdido' ? 'active' : ''}`}
            onClick={() => handleStatusClick('Perdido')}
            style={{ color: selectedStatus === 'Perdido' ? '#ff2a4e' : 'gray' }}
          />
        </div> */}


        <div className='status-container-dashboard'>



          <div className='btn-dashboard-vendido-perdido-vendas'>
            <MdThumbUp
              className={`icon-perdido-dashboard ${selectedStatus === 'Vendido' ? 'active' : ''}`}
              onClick={() => handleStatusClick('Vendido')}
              style={{ color: selectedStatus === 'Vendido' ? 'dodgerblue' : 'gray' }}
            />
            <label className='label-btn-dashboard-vendido-perdido-vendas'>Vendidos</label>
          </div>

          <div className='btn-dashboard-vendido-perdido-vendas'>

            <MdThumbDown
              className={`icon-vendido-dashboard ${selectedStatus === 'Perdido' ? 'active' : ''}`}
              onClick={() => handleStatusClick('Perdido')}
              style={{ color: selectedStatus === 'Perdido' ? '#ff2a4e' : 'gray' }}
            />

            <label className='label-btn-dashboard-vendido-perdido-vendas'>Perdidos</label>
          </div>

        </div>








        <div className='status-container-dashboard'>
          <div className='btn-dashboard-vendido-perdido-vendas'>

            <MdAddShoppingCart
              className={`icon-perdido-dashboard ${selectedStatus === 'Pedido de Venda' ? 'active' : ''}`}
              onClick={() => handleStatusClick('Pedido de Venda')}
              style={{ color: selectedStatus === 'Pedido de Venda' ? '#ffc107' : 'gray' }}
            />
            <label className='label-btn-dashboard-vendido-perdido-vendas'>Pedido Efetuado</label>
          </div>

          <div className='btn-dashboard-vendido-perdido-vendas'>

            <MdDescription
              className={`icon-perdido-dashboard ${selectedStatus === 'Orçamento' ? 'active' : ''}`}
              onClick={() => handleStatusClick('Orçamento')}
              style={{ color: selectedStatus === 'Orçamento' ? '#17a2b8' : 'gray' }}
            />
            <label className='label-btn-dashboard-vendido-perdido-vendas'>Orçamentos</label>
          </div>
        </div>

        <div className='status-container-dashboard'>
          <div className='btn-dashboard-vendido-perdido-vendas'>

            <MdFilterAltOff
              className={`icon-limpar-dashboard`}
              onClick={() => clearListas()}
              style={{ color: 'gray' }}
            />
            <label className='label-btn-dashboard-vendido-perdido-vendas'>Limpar Filtro</label>
          </div>
          <div className='btn-dashboard-vendido-perdido-vendas'>
            <MdBarChart
              className='icon-limpar-dashboard'
              onClick={() => setShowAnalyticsModal(true)}
              style={{ color: '#17a2b8' }}
            />
            <label className='label-btn-dashboard-vendido-perdido-vendas'>Gráficos</label>
          </div>
        </div>




        {/* <div className='status-container-dashboard'>
          <MdFilterAltOff
            className={`icon-perdido-dashboard `}
            onClick={() => clearListas()}
            style={{ color: 'gray' }}
          />

          <div style={{ backgroundColor: 'transparent' }} className="icon-vendido-dashboard"></div>


        </div> */}



        <div className='metric-switch-container'>
          <label className='title-value-dash'>Valor:</label>
          <button
            style={{ backgroundColor: selectedMetric == 'cost_value' ? 'red' : '' }}
            className='btn-cost-value'
            onClick={() => setSelectedMetric('cost_value')}
          >
            Custo
          </button>
          <button
            style={{ backgroundColor: selectedMetric == 'sale_value' ? 'red' : '' }}
            className='btn-sale-value'
            onClick={() => setSelectedMetric('sale_value')}
          >
            Venda
          </button>
        </div>


        <div className='conversion-card-group'>
          <div className='conversion-card'>
            <span className='conversion-label'>Taxa de Conversão</span>
            <strong className='conversion-value'>{conversionRate.toFixed(2)}%</strong>
            <small className='conversion-subtitle'>Pedidos vs Orçamentos</small>
          </div>
          <div className='conversion-card'>
            <span className='conversion-label'>Desconto Médio</span>
            <strong className='conversion-value'>
              {averageDiscount.toFixed(2)}%
            </strong>
            <small className='conversion-subtitle'>Pedidos efetuados</small>
          </div>
        </div>




      </div>
      {dashboardError && (
        <div className='dashboard-feedback dashboard-feedback-error'>
          {dashboardError}
        </div>
      )}
      {isDashboardBusy && !dashboardError && (
        <div className='dashboard-feedback dashboard-feedback-loading'>
          Carregando dados do dashboard...
        </div>
      )}
      <div className='dashboard-body'>


        <div className='dashboard-afilhados-list'>
          <table className='table-dashboard'>
            <thead>
              <tr>
                <th>Nome</th>
                <th className='hidden-col'>Proporção</th>
                <th>Valor Total</th>
                <th>%</th>
              </tr>
            </thead>
            <tbody>
              {totalSalesData.map((afilhado, index) => (
                <tr
                  key={afilhado.id}
                  onClick={() => handleAfilhadoClick(afilhado)}
                  className={selectedAfilhados.includes(afilhado.id) ? 'selected-row' : ''}
                >
                  <td className='td-dashboard' width={'200px'} style={{ maxWidth: '200px' }}>{formatName(afilhado.username)}</td>
                  <td className='bar-container hidden-col'>


                    <div className='bar' style={{
                      backgroundColor: selectedStatus === 'Perdido' ? '#ff2a4e' : 'dodgerblue',
                      width: `${(afilhado.percentageSold / maxPercentage) * 100 || 0}%`
                    }}></div>


                  </td>
                  <td className='td-dashboard'>{formatValue(afilhado.totalSales)}</td>
                  <td className='td-dashboard'>{(afilhado.percentageSold || 0).toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table >
        </div>

        <div className='dashboard-states-list'>
          <table className='table-dashboard'>
            <thead>
              <tr>
                <th style={{ maxWidth: '10px' }}>UF</th>
                <th>Valor Total</th>
                <th>%</th>
              </tr>
            </thead>
            <tbody>
              {filteredTotalSalesDataByState.map((state, index) => (
                <tr
                  key={state.state}
                  onClick={() => handleStateClick(state.state)}
                  className={selectedStates.includes(state.state) ? 'selected-row' : ''}
                >
                  <td className='td-dashboard' width={'20px'}>{state.state}</td>
                  <td className='td-dashboard' >{formatValue(state.totalSales)}</td>
                  <td className='td-dashboard'>{(state.percentageSold || 0).toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className='dashboard-cities-list'>
          <table className='table-dashboard'>
            <thead>
              <tr>
                <th>Cidade</th>
                <th>Valor Total</th>
                <th>%</th>
              </tr>
            </thead>
            <tbody>
              {filteredTotalSalesDataByCity.map((city, index) => (
                <tr
                  key={city.city}
                  onClick={() => handleCityClick(city.city)}
                  className={selectedCities.includes(city.city) ? 'selected-row' : ''}
                >
                  <td className='td-dashboard' width={'200px'}>{city.city}</td>
                  <td className='td-dashboard'>{formatValue(city.totalSales)}</td>
                  <td className='td-dashboard'>{(city.percentageSold || 0).toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className='dashboard-products-list'>
          <table className='table-dashboard'>
            <thead>
              <tr>
                <th>Produto</th>
                <th>Valor Total</th>
                <th>%</th>
              </tr>
            </thead>
            <tbody>
              {filteredTotalSalesDataByProduct.map((product, index) => (
                <tr
                  key={product.produto}
                  onClick={() => handleProductClick(product.produto)}
                  className={selectedProducts.includes(product.produto) ? 'selected-row' : ''}
                >
                  <td className='td-dashboard' width={'200px'}>{truncateText(product.produto, 20)}</td>
                  <td className='td-dashboard'>{formatValue(product.totalSales)}</td>
                  <td className='td-dashboard'>{(product.percentageSold || 0).toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className='dashboard-origins-list'>
          <table className='table-dashboard'>
            <thead>
              <tr>
                <th>Origem</th>
                <th>Valor Total</th>
                <th>%</th>
              </tr>
            </thead>
            <tbody>
              {filteredTotalSalesDataByOrigin.map((origem, index) => (
                <tr
                  key={origem.origem}
                  onClick={() => handleOriginClick(origem.origem)}
                  className={selectedOrigins.includes(origem.origem) ? 'selected-row' : ''}
                >
                  <td className='td-dashboard' width={'200px'}>{origem.origem}</td>
                  <td className='td-dashboard' >{formatValue(origem.totalSales)}</td>
                  <td className='td-dashboard' >{(origem.percentageSold || 0).toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>


        <div className='dashboard-cards-list'>
          <table className="table-dashboard">
            <thead>
              <tr>
                <th>Nome do Cliente</th>
                <th>Valor</th>
                <th>%</th>
              </tr>
            </thead>
            <tbody>
              {sortedCardsByPercentage.map((card, index) => (
                <tr
                  key={card.id}
                  onClick={(e) => {
                    e.stopPropagation(); // Previne a propagação do clique
                    handleCardClick(card); // Define o card selecionado
                  }}
                  style={{
                    backgroundColor: index % 2 === 0 ? '#f0f0f0' : '#ffffff',
                  }}
                >
                  <td className="td-dashboard">  {card.name.length > 20 ? card.name.substring(0, 20) + '' : card.name}
                  </td>
                  <td className="td-dashboard">

                    {formatValue(parseFloat(card[selectedMetric]))}


                    {/* {parseFloat(card.cost_value).toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    })} */}


                  </td>
                  <td className="td-dashboard">
                    {card.percentageSold.toFixed(2)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Modal fora do loop */}
        {showPreviewCard && selectedCard && (
          <div className='preview-card-dashboard-modal'>
            <div className='preview-card-dashboard-container'>
              <button onClick={() => setShowPreviewCard(false)} className="close-modal-button">
                Fechar
              </button>
              <PreviewCard key={selectedCard.id} cardData={selectedCard} />
            </div>
          </div>
        )}

        {showAnalyticsModal && (
          <div className='dashboard-analytics-modal'>
            <div className='dashboard-analytics-container'>
              <button
                className='dashboardpage-analytics-close-fixed'
                onClick={() => setShowAnalyticsModal(false)}
                title='Fechar'
              >
                <MdClose />
              </button>
              <div className='dashboard-analytics-periods'>
                <div className='dashboard-analytics-period'>
                  <h5>Período A</h5>
                  <div className='dashboard-analytics-period-row'>
                    <div className='dashboard-analytics-period-field'>
                      <label>Data Inicial</label>
                      <input
                        type="date"
                        value={compareStartDateA}
                        onChange={(e) => setCompareStartDateA(e.target.value)}
                      />
                    </div>
                    <div className='dashboard-analytics-period-field'>
                      <label>Data Final</label>
                      <input
                        type="date"
                        value={compareEndDateA}
                        onChange={(e) => setCompareEndDateA(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className='dashboard-analytics-period'>
                  <h5>Período B</h5>
                  <div className='dashboard-analytics-period-row'>
                    <div className='dashboard-analytics-period-field'>
                      <label>Data Inicial</label>
                      <input
                        type="date"
                        value={compareStartDateB}
                        onChange={(e) => setCompareStartDateB(e.target.value)}
                      />
                    </div>
                    <div className='dashboard-analytics-period-field'>
                      <label>Data Final</label>
                      <input
                        type="date"
                        value={compareEndDateB}
                        onChange={(e) => setCompareEndDateB(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className='dashboard-analytics-actions'>
                  <div className="dashboardpage-analytics-actions-stack">
                    <div className="dashboardpage-analytics-actions-top">
                      <div className="dashboardpage-analytics-source-toggle">
                        <span className="dashboardpage-analytics-source-label">Fonte:</span>
                        <button
                          type="button"
                          className={`dashboardpage-analytics-source-btn ${analyticsSource === 'crm' ? 'is-active' : ''}`}
                          onClick={() => setAnalyticsSource('crm')}
                        >
                          CRM
                        </button>
                        <button
                          type="button"
                          className={`dashboardpage-analytics-source-btn ${analyticsSource === 'faktory' ? 'is-active' : ''}`}
                          onClick={() => setAnalyticsSource('faktory')}
                          disabled={!isAdmin}
                          title={!isAdmin ? 'Acesso restrito a administradores' : 'Faktory'}
                          style={{ opacity: !isAdmin ? 0.5 : 1, cursor: !isAdmin ? 'not-allowed' : 'pointer' }}
                        >
                          Faktory
                        </button>
                      </div>
                      <div className="dashboardpage-analytics-actions-top-right">
                        {analyticsError && (
                          <span className='dashboard-analytics-error'>{analyticsError}</span>
                        )}
                      </div>
                    </div>

                    <div className="dashboardpage-analytics-actions-filters">
                      {analyticsSource === 'crm' ? (
                        <div className="dashboardpage-analytics-filter-block">
                          <div className="dashboardpage-analytics-filter-header">
                            <span className="dashboardpage-analytics-filter-title">Produtos</span>
                            <div className="dashboardpage-analytics-filter-actions">
                              <button
                                type="button"
                                className="dashboardpage-analytics-filter-action"
                                onClick={selectAllCrmProductsAnalytics}
                                title="Selecionar todos"
                                disabled={!crmProductOptionsForAnalytics.length}
                              >
                                <FaCheckDouble />
                              </button>
                              <button
                                type="button"
                                className="dashboardpage-analytics-filter-action"
                                onClick={clearCrmProductAnalyticsSelection}
                                title="Limpar seleção"
                                disabled={selectedCrmProductsForAnalytics.length === 0}
                              >
                                <FaEraser />
                              </button>
                            </div>
                          </div>
                          <div className="dashboardpage-analytics-filter-list">
                            {crmProductOptionsForAnalytics.map((p) => (
                              <button
                                key={p}
                                type="button"
                                className={
                                  selectedCrmProductsForAnalytics.includes(p)
                                    ? 'dashboardpage-analytics-filter-pill is-selected'
                                    : 'dashboardpage-analytics-filter-pill'
                                }
                                onClick={() => handleCrmProductAnalyticsClick(p)}
                                title={p}
                              >
                                {p}
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="dashboardpage-analytics-filter-block">
                          <div className="dashboardpage-analytics-filter-header">
                            <span className="dashboardpage-analytics-filter-title">Tipos</span>
                            <div className="dashboardpage-analytics-filter-actions">
                              <button
                                type="button"
                                className="dashboardpage-analytics-filter-action"
                                onClick={selectAllFaktoryDocumentTypes}
                                title="Selecionar todos"
                                disabled={!faktoryDocumentTypes.length}
                              >
                                <FaCheckDouble />
                              </button>
                              <button
                                type="button"
                                className="dashboardpage-analytics-filter-action"
                                onClick={clearFaktoryDocumentTypes}
                                title="Limpar seleção"
                                disabled={selectedFaktoryDocumentTypes.length === 0}
                              >
                                <FaEraser />
                              </button>
                            </div>
                          </div>
                          <div className="dashboardpage-analytics-filter-list">
                            {(faktoryDocumentTypes || []).map((d) => {
                              const cd = String(d.cd_documento || d.CD_DOCUMENTO || '').trim();
                              const ds = String(d.ds_documento || d.DS_DOCUMENTO || cd).trim();
                              if (!cd) return null;
                              return (
                                <button
                                  key={cd}
                                  type="button"
                                  className={
                                    selectedFaktoryDocumentTypes.includes(cd)
                                      ? 'dashboardpage-analytics-filter-pill is-selected'
                                      : 'dashboardpage-analytics-filter-pill'
                                  }
                                  onClick={() => handleFaktoryDocumentTypeClick(cd)}
                                  title={ds || cd}
                                >
                                  {ds || cd}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className='dashboard-analytics-period-shortcuts'>
                  <button
                    className='dashboard-analytics-shortcut-btn'
                    onClick={setPeriodMonthPrevious}
                    title='Mês atual vs mês anterior (mesmo ano)'
                  >
                    Mês Anterior
                  </button>
                  <button
                    className='dashboard-analytics-shortcut-btn'
                    onClick={setPeriodMonthLastYear}
                    title='Mês atual vs mesmo mês do ano passado'
                  >
                    Mês Ano Passado
                  </button>
                  <button
                    className='dashboard-analytics-shortcut-btn'
                    onClick={setPeriodQuarterPrevious}
                    title='Trimestre atual vs trimestre anterior (mesmo ano)'
                  >
                    Trimestre Anterior
                  </button>
                  <button
                    className='dashboard-analytics-shortcut-btn'
                    onClick={setPeriodQuarterLastYear}
                    title='Trimestre atual vs mesmo trimestre do ano passado'
                  >
                    Trimestre Ano Passado
                  </button>
                  <button
                    className='dashboard-analytics-shortcut-btn'
                    onClick={setPeriodSemesterPrevious}
                    title='Semestre atual vs semestre anterior (mesmo ano)'
                  >
                    Semestre Anterior
                  </button>
                  <button
                    className='dashboard-analytics-shortcut-btn'
                    onClick={setPeriodSemesterLastYear}
                    title='Semestre atual vs mesmo semestre do ano passado'
                  >
                    Semestre Ano Passado
                  </button>
                  <button
                    className='dashboard-analytics-shortcut-btn'
                    onClick={setPeriodYear}
                    title='Ano atual completo vs ano passado completo'
                  >
                    Ano
                  </button>
                  <button
                    className='dashboard-analytics-refresh dashboardpage-analytics-refresh-inline'
                    onClick={fetchAnalyticsData}
                    disabled={isLoadingAnalytics}
                    title={isLoadingAnalytics ? 'Atualizando...' : 'Atualizar gráficos'}
                  >
                    <MdRefresh className={`dashboard-analytics-refresh-icon ${isLoadingAnalytics ? 'is-rotating' : ''}`} />
                  </button>
                </div>
              </div>
              <div className='dashboard-analytics-content'>
                {analyticsSource === 'crm' ? (
                  <div className='dashboard-analytics-entities'>
                    <div className='dashboard-analytics-entities-header'>
                      <h4>Entidades</h4>
                      <div className='dashboard-analytics-entities-actions'>
                        <button
                          className='dashboard-analytics-entities-action'
                          onClick={handleSelectAllAnalyticsEntities}
                          title='Selecionar todos'
                        >
                          <FaCheckDouble />
                        </button>
                        <button
                          className='dashboard-analytics-entities-action'
                          onClick={handleClearAnalyticsSelection}
                          title='Limpar seleção'
                          disabled={selectedAnalyticsEntities.length === 0}
                        >
                          <FaEraser />
                        </button>
                      </div>
                    </div>
                    <ul>
                      {dashboardTeamUsers.map((entity) => (
                        <li
                          key={entity.id}
                          onClick={() => handleAnalyticsEntityClick(entity.id)}
                          className={selectedAnalyticsEntities.includes(entity.id) ? 'dashboard-analytics-entity-selected' : ''}
                        >
                          {entity.username || entity.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="dashboardpage-analytics-faktory-panel">
                    <div className="dashboardpage-analytics-faktory-header">
                      <h4 className="dashboardpage-analytics-faktory-title">Vendedores</h4>
                      <div className="dashboardpage-analytics-faktory-actions">
                        <button
                          type="button"
                          className="dashboardpage-analytics-faktory-action"
                          onClick={handleSelectAllFaktoryVendors}
                          title="Selecionar todos"
                          disabled={!faktoryVendorOptions.length}
                        >
                          <FaCheckDouble />
                        </button>
                        <button
                          type="button"
                          className="dashboardpage-analytics-faktory-action"
                          onClick={handleClearFaktoryVendors}
                          title="Limpar seleção"
                          disabled={selectedFaktoryVendors.length === 0}
                        >
                          <FaEraser />
                        </button>
                      </div>
                    </div>
                    <p className="dashboardpage-analytics-faktory-subtitle">
                      Dados do Faktory (Pedido de Venda), agrupados por vendedor.
                    </p>
                    <ul className="dashboardpage-analytics-faktory-list">
                      {faktoryVendorOptions.map((vendor) => (
                        <li
                          key={vendor.id}
                          onClick={() => handleFaktoryVendorClick(vendor.id)}
                          className={
                            selectedFaktoryVendors.includes(vendor.id)
                              ? 'dashboardpage-analytics-faktory-item is-selected'
                              : 'dashboardpage-analytics-faktory-item'
                          }
                        >
                          {vendor.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className='dashboard-analytics-visuals'>
                  <div className='dashboard-analytics-summary'>
                    <div className='dashboard-analytics-summary-card'>
                      <span className='dashboard-analytics-summary-label'>Período A</span>
                      <strong className='dashboard-analytics-summary-value'>{formatValue(analyticsGroupTotals.periodA)}</strong>
                      <span className='dashboard-analytics-summary-meta'>
                        Meta: {formatValue(analyticsGroupTotals.metaPeriodA)}
                      </span>
                      <span className='dashboard-analytics-summary-meta'>
                        % Meta atingida: {analyticsGroupTotals.achievementA.toFixed(1)}%
                      </span>
                    </div>
                    <div className='dashboard-analytics-summary-card'>
                      <span className='dashboard-analytics-summary-label'>Período B</span>
                      <strong className='dashboard-analytics-summary-value'>{formatValue(analyticsGroupTotals.periodB)}</strong>
                      <span className='dashboard-analytics-summary-meta'>
                        Meta: {formatValue(analyticsGroupTotals.metaPeriodB)}
                      </span>
                      <span className='dashboard-analytics-summary-meta'>
                        % Meta atingida: {analyticsGroupTotals.achievementB.toFixed(1)}%
                      </span>
                    </div>
                    <div className='dashboard-analytics-summary-card'>
                      <span className='dashboard-analytics-summary-label'>Diferença B x A</span>
                      <strong className='dashboard-analytics-summary-value'>{formatValue(analyticsGroupTotals.diffValue)}</strong>
                      <span className='dashboard-analytics-summary-meta'>
                        % Diferença: {analyticsGroupTotals.diffPercent.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  {isLoadingAnalytics ? (
                    <p>Carregando...</p>
                  ) : analyticsComparisonList.length > 0 ? (
                    <div className='dashboard-analytics-list'>
                      {analyticsComparisonList.map((item) => (
                        <div key={item.id} className='dashboard-analytics-user'>
                          <div className='dashboard-analytics-user-header'>
                            <span className='dashboard-analytics-user-name'>{item.name}</span>
                              <span className='dashboard-analytics-user-discount'>
                                Meta A: {item.achievementA.toFixed(1)}% | Meta B: {item.achievementB.toFixed(1)}% | Dif.: {item.diffPercent.toFixed(1)}%
                              </span>
                          </div>
                          <div className='dashboard-analytics-user-bars'>
                            <div className='dashboard-analytics-bar-row'>
                              <div className='dashboard-analytics-bar-label'>
                                <span>Meta Período A</span>
                                <span className='dashboard-analytics-bar-value'>{formatValue(item.metaPeriodA)}</span>
                              </div>
                              <div className='dashboard-analytics-bar meta' style={{ width: `${item.metaPeriodAWidth}%` }} />
                            </div>
                            <div className='dashboard-analytics-bar-row'>
                              <div className='dashboard-analytics-bar-label'>
                                <span>Período A</span>
                                <span className='dashboard-analytics-bar-value'>{formatValue(item.periodA)}</span>
                              </div>
                              <div className='dashboard-analytics-bar period-a' style={{ width: `${item.periodAWidth}%` }} />
                            </div>
                            <div className='dashboard-analytics-bar-row'>
                              <div className='dashboard-analytics-bar-label'>
                                <span>Meta Período B</span>
                                <span className='dashboard-analytics-bar-value'>{formatValue(item.metaPeriodB)}</span>
                              </div>
                              <div className='dashboard-analytics-bar meta' style={{ width: `${item.metaPeriodBWidth}%` }} />
                            </div>
                            <div className='dashboard-analytics-bar-row'>
                              <div className='dashboard-analytics-bar-label'>
                                <span>Período B</span>
                                <span className='dashboard-analytics-bar-value'>{formatValue(item.periodB)}</span>
                              </div>
                              <div className='dashboard-analytics-bar period-b' style={{ width: `${item.periodBWidth}%` }} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className='dashboard-analytics-placeholder'>
                      Defina os períodos para visualizar os gráficos.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}



      </div>
    </div>
  );
}

export default DashboardPage;
